/**
 * One animated tournament scenario (mode « Simulation »).
 *
 * Plays a SINGLE tournament from the chosen model — the legacy "Play" feature,
 * but driven by the ported engine. Two scenario types:
 *  - "modal"  : every match takes its most-probable exact score (deterministic);
 *  - "random" : every match is sampled from the Elo–Poisson law (seeded RNG),
 *               knockout ties go to extra time then a shoot-out.
 *
 * Reuses the SAME derivation as the official dashboard (`deriveOfficialState`)
 * for ranking + bracket propagation: scores are generated round by round, fed
 * into a synthetic LiveData, and re-derived so the next round's teams are known.
 * Because the derivation is parameterised by the model's Elo + draw seed, the
 * resulting OfficialState animates exactly like the live one (groups, thirds,
 * bracket, living Elo) — no duplicated logic.
 */
import { deriveOfficialState, type OfficialState } from "./officialStore.ts";
import {
  buildTournamentStructure,
  gkRatings,
  gkDivisor,
  hosts,
  initialElos,
  legacyElos,
  modelHomeAdvantage,
  poissonParams,
} from "./data.ts";
import type { LiveData, LiveMatchEntry } from "./liveSchema.ts";
import { drawExtraTimeScore, drawScore, modalScore } from "../engine/poisson.ts";
import { shootoutWinProbability, type ShootoutModel } from "../engine/shootout.ts";
import { hashSeed, mulberry32 } from "../engine/rng.ts";
import type { FixedResult } from "../engine/montecarlo.ts";
import type { Slot } from "../engine/bracket.ts";
import type { EloTable, HostsTable, Score, TeamCode } from "../engine/types.ts";

export type ScenarioType = "modal" | "random";
/** real = eloratings ; legacy = Elo v2 ; value = Elo mélangé à la valeur d'effectif. */
export type RatingsModel = "real" | "legacy" | "value";
export type StartFrom = "scratch" | "fromReal";

export interface ScenarioOptions {
  /** "modal" = most-probable score (deterministic) ; "random" = sampled. */
  readonly scenarioType: ScenarioType;
  /** Starting ratings: real (eloratings.net) or the legacy v2 values. */
  readonly ratings: RatingsModel;
  /** Whole tournament, or fix the real results so far and simulate the rest. */
  readonly startFrom: StartFrom;
  readonly shootoutModel: ShootoutModel;
  /** Seed for the "random" scenario (a new value = a new draw). */
  readonly seed: string;
  /** Real results to fix when startFrom === "fromReal" (FIFA id → result). */
  readonly fixedResults?: Readonly<Record<number, FixedResult>>;
  /** Elo table blended with squad value — used when ratings === "value". */
  readonly valueElos?: EloTable;
}

export interface ScenarioResult {
  readonly official: OfficialState;
  readonly live: LiveData;
  readonly champion: TeamCode | null;
  readonly runnerUp: TeamCode | null;
  readonly third: TeamCode | null;
  /** Elo table the scenario was built on (so the UI can label the model). */
  readonly elos: EloTable;
  readonly drawSeed: string;
}

interface KoOutcome {
  readonly score: Score;
  readonly winnerSlot: Slot;
  readonly penScore: Score;
}

/** Synthetic timestamp — keeps `simulateScenario` pure (no Date) for tests. */
const SIM_GENERATED_AT = "2026-01-01T00:00:00.000Z";

function finishedEntry(id: number, score: Score, penalty: KoOutcome | null): LiveMatchEntry {
  const tie = penalty !== null && score[0] === score[1];
  return {
    id,
    status: "finished",
    minute: null,
    score: { ft: score },
    extraTime: null,
    penalties: tie
      ? { score: penalty.penScore, winner: penalty.winnerSlot === "a" ? "home" : "away" }
      : null,
    events: [],
  };
}

function fixedOutcome(f: FixedResult): KoOutcome {
  const [ga, gb] = f.score;
  const winnerSlot: Slot = ga > gb ? "a" : gb > ga ? "b" : f.penaltiesWinner === "away" ? "b" : "a";
  return { score: f.score, winnerSlot, penScore: winnerSlot === "a" ? [5, 4] : [4, 5] };
}

export function simulateScenario(opts: ScenarioOptions): ScenarioResult {
  const structure = buildTournamentStructure();
  const elos: EloTable =
    opts.ratings === "legacy"
      ? legacyElos
      : opts.ratings === "value" && opts.valueElos
        ? opts.valueElos
        : initialElos;
  const drawSeed = `sim:${opts.seed}`;
  const rng = mulberry32(hashSeed(opts.seed));
  const fixed: Readonly<Record<number, FixedResult>> =
    (opts.startFrom === "fromReal" ? opts.fixedResults : undefined) ?? {};
  const params = poissonParams;
  const hostsTable: HostsTable = hosts;

  const effElo = (team: TeamCode, hostCountry: string | null): number => {
    const base = elos[team] ?? 0;
    return hostCountry !== null && hostsTable[team] === hostCountry ? base + modelHomeAdvantage : base;
  };

  const playKo = (a: TeamCode, b: TeamCode, hostCountry: string | null): KoOutcome => {
    const ea = effElo(a, hostCountry);
    const eb = effElo(b, hostCountry);
    if (opts.scenarioType === "modal") {
      const ft = modalScore(ea, eb, params);
      if (ft[0] !== ft[1]) return { score: ft, winnerSlot: ft[0] > ft[1] ? "a" : "b", penScore: [0, 0] };
      // Modal tie → the favourite (higher effective Elo) advances, deterministically.
      const winnerSlot: Slot = ea >= eb ? "a" : "b";
      return { score: ft, winnerSlot, penScore: winnerSlot === "a" ? [5, 4] : [4, 5] };
    }
    const ft = drawScore(ea, eb, params, rng);
    if (ft[0] !== ft[1]) return { score: ft, winnerSlot: ft[0] > ft[1] ? "a" : "b", penScore: [0, 0] };
    const et = drawExtraTimeScore(ea, eb, params, rng);
    const total: Score = [ft[0] + et[0], ft[1] + et[1]];
    if (total[0] !== total[1]) return { score: total, winnerSlot: total[0] > total[1] ? "a" : "b", penScore: [0, 0] };
    const pHome = shootoutWinProbability(a, b, opts.shootoutModel, gkRatings, gkDivisor);
    const winnerSlot: Slot = rng() < pHome ? "a" : "b";
    return { score: total, winnerSlot, penScore: winnerSlot === "a" ? [5, 4] : [4, 5] };
  };

  const entries: LiveMatchEntry[] = [];
  const buildLive = (): LiveData => ({
    schemaVersion: 1,
    generatedAt: SIM_GENERATED_AT,
    source: "simulation",
    matches: [...entries],
  });

  // --- group stage ----------------------------------------------------------
  for (const m of structure.groupMatches) {
    const f = fixed[m.id];
    const score: Score = f
      ? f.score
      : opts.scenarioType === "modal"
        ? modalScore(effElo(m.home, m.hostCountry), effElo(m.away, m.hostCountry), params)
        : drawScore(effElo(m.home, m.hostCountry), effElo(m.away, m.hostCountry), params, rng);
    entries.push(finishedEntry(m.id, score, null));
  }

  // --- knockout: score every node whose teams are resolved, re-derive, repeat
  let official = deriveOfficialState(buildLive(), { elos, drawSeed });
  const scored = new Set<number>();
  let guard = 0;
  while (scored.size < structure.koMatches.length && guard++ < 12) {
    let progressed = false;
    for (const ko of structure.koMatches) {
      if (scored.has(ko.id)) continue;
      const node = official.bracket[ko.nodeId];
      if (!node || node.a === null || node.b === null) continue;
      const f = fixed[ko.id];
      const outcome = f ? fixedOutcome(f) : playKo(node.a, node.b, ko.hostCountry);
      entries.push(finishedEntry(ko.id, outcome.score, outcome));
      scored.add(ko.id);
      progressed = true;
    }
    official = deriveOfficialState(buildLive(), { elos, drawSeed });
    if (!progressed) break;
  }
  // Invariant : tous les nœuds KO doivent être joués à la sortie. Sinon
  // (régression de propagation / table bracket incohérente) on échoue FORT
  // plutôt que de renvoyer un podium null silencieux.
  if (scored.size < structure.koMatches.length) {
    throw new Error(
      `simulateScenario: bracket incomplet (${scored.size}/${structure.koMatches.length}) — propagation bloquée`,
    );
  }

  const finalNode = official.bracket["M104"];
  const thirdNode = official.bracket["M103"];
  const champion = finalNode?.winner ?? null;
  const runnerUp = finalNode ? (finalNode.winner === finalNode.a ? finalNode.b : finalNode.a) : null;
  const third = thirdNode?.winner ?? null;

  return { official, live: buildLive(), champion, runnerUp, third, elos, drawSeed };
}
